class SideCart extends HTMLElement {
  constructor() {
    super();
    this.onCartUpdated = this.onCartUpdated.bind(this);
    this._deliveryTimer = null;
    this._guaranteeTimer = null;
    this._boundOpeners = false;
  }

  connectedCallback() {
    this.refreshRefs();
    this.bindStaticEvents();
    this.addEventListener('click', this.onClick.bind(this));
    document.addEventListener('cart:updated', this.onCartUpdated);
    this.initAll();
    this.maybeAutoOpen();
  }

  maybeAutoOpen() {
    const params = new URLSearchParams(window.location.search);
    if (params.get('open-cart') !== '1') return;
    this.open();
    params.delete('open-cart');
    const query = params.toString();
    const newUrl = window.location.pathname + (query ? '?' + query : '') + window.location.hash;
    window.history.replaceState({}, '', newUrl);
  }

  disconnectedCallback() {
    document.removeEventListener('cart:updated', this.onCartUpdated);
    document.removeEventListener('keydown', this._onKeydown);
    this.stopTimers();
  }

  refreshRefs() {
    this.closeButtons = this.querySelectorAll('[data-sidecart-close]');
    this.openButtons = document.querySelectorAll('.header__sidecart-button');
  }

  bindStaticEvents() {
    if (!this._boundOpeners) {
      this.openButtons.forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open();
        })
      );
      this._boundOpeners = true;
    }

    this.rebindCloseButtons();

    this._onKeydown = (e) => {
      if (e.key !== 'Escape') return;

      if (this.isPreviewModalOpen()) {
        this.closePreviewPopup();
        return;
      }

      if (this.isOpen()) this.close();
    };
    document.addEventListener('keydown', this._onKeydown);
  }

  rebindCloseButtons() {
    this.closeButtons.forEach((btn) => {
      btn.onclick = (e) => {
        e.preventDefault();
        this.close();
      };
    });
  }

  stopTimers() {
    if (this._deliveryTimer) clearInterval(this._deliveryTimer);
    if (this._guaranteeTimer) clearInterval(this._guaranteeTimer);
    this._deliveryTimer = null;
    this._guaranteeTimer = null;
  }

  initAll() {
    this.initDeliveryBlocks();
    this.initGuaranteeSlider();
    this.initDiscountInput();
  }

  initDiscountInput() {
    const input = this.querySelector('.sidecart__discount-input');
    const btn = this.querySelector('.sidecart__discount-button');
    if (!input || !btn) return;
    input.addEventListener('input', () => {
      btn.classList.toggle('is-active', input.value.trim().length > 0);
    });
  }

  async addSupProduct(btn) {
    const card = btn.closest('[data-product-sup]');
    const id = card?.dataset.variantId;
    if (!id) return;
  
    btn.disabled = true;
  
    try {
      const r = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ id: Number(id), quantity: 1 })
      });
  
      if (!r.ok) throw new Error('add failed');
  
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (e) {
      console.error(e);
      btn.disabled = false;
    }
  }
  

  async onCartUpdated() {
    await this.reloadSection();
    this.initAll();
    this.open();
  }

  onClick(e) {
    const t = (sel) => e.target.closest(sel);

    const qtyBtn = t('[data-qty-btn]');
    if (qtyBtn) return this.onQtyClick(qtyBtn);

    const applyBtn = t('[data-sidecart-apply-discount]');
    if (applyBtn) return this.applyDiscountFromInput();

    const supAtc = e.target.closest('[data-sup-atc]');
    if (supAtc) return this.addSupProduct(supAtc);

    const removeBtn = t('[data-sidecart-remove-discount]');
    if (removeBtn) return this.removeDiscount();

    const toggleBtn = t('[data-discount-toggle]');
    if (toggleBtn) return this.toggleDiscountPanel(toggleBtn);

    const gItem = t('.sc-guarantee__pagination-item');
    if (gItem) return this.onGuaranteeClick(gItem);

    const previewBtn = t('[data-preview-popup]');
    if (previewBtn) return this.openPreviewPopup(previewBtn);
  }

  isPreviewModalOpen() {
    const modal = document.querySelector('.side-cart__gallery-modal');
    return Boolean(modal && !modal.hasAttribute('hidden'));
  }

  getPreviewSources(btn) {
    const raw = btn.dataset.previewSrc;
    if (!raw) return [];

    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed.filter(Boolean);
    } catch (e) {
      // keep string fallback
    }

    return [raw];
  }

  openPreviewPopup(btn) {
    const sources = this.getPreviewSources(btn);
    if (!sources.length) return;

    const wrapper = document.querySelector('.side-cart__panel');
    let modal = document.querySelector('.side-cart__gallery-modal');
    if (!modal) {
      modal = document.createElement('div');
      modal.className = 'mp-gallery-modal side-cart__gallery-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-label', 'Galerie photos');
      modal.setAttribute('hidden', '');
      modal.innerHTML = `
        <div class="mp-gallery-modal__header">
          <button class="mp-gallery-modal__close" type="button" aria-label="Fermer la galerie">&#x2715;</button>
        </div>
        <div class="mp-gallery-modal__scroll"></div>
      `;
      wrapper.appendChild(modal);

      const closeBtn = modal.querySelector('.mp-gallery-modal__close');
      closeBtn?.addEventListener('click', () => this.closePreviewPopup());
    }

    const scrollEl = modal.querySelector('.mp-gallery-modal__scroll');
    if (!scrollEl) return;

    scrollEl.innerHTML = '';
    sources.forEach((src) => {
      const image = document.createElement('img');
      image.src = src;
      image.alt = 'Aperçu personnalisation';
      image.className = 'mp-gallery-modal__image';
      scrollEl.appendChild(image);
    });

    scrollEl.scrollTop = 0;
    modal.removeAttribute('hidden');
    document.body.style.overflow = 'hidden';
  }

  closePreviewPopup() {
    const modal = document.querySelector('.side-cart__gallery-modal');
    if (modal) {
      modal.setAttribute('hidden', '');
      document.body.style.overflow = '';
    }
  }

  toggleDiscountPanel(btn) {
    const panel = this.querySelector('[data-discount-panel]');
    if (!panel) return;
    const isOpen = panel.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  async applyDiscountFromInput() {
    const input = this.querySelector('.sidecart__discount-input');
    const msg = this.querySelector('[data-discount-message]');
    if (!input) return;

    const code = input.value.trim();
    if (msg) {
      msg.textContent = '';
      msg.classList.remove('is-error', 'is-success');
    }
    if (!code) {
      if (msg) {
        msg.textContent = 'Veuillez saisir un code promo.';
        msg.classList.add('is-error');
      }
      return;
    }

    try {
      const res = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: code }),
      });
      const cart = await res.json();
      const lc = code.toLowerCase();

      let ok =
        Array.isArray(cart.cart_level_discount_applications) &&
        cart.cart_level_discount_applications.some((a) => (a.title || '').toLowerCase() === lc);

      if (!ok && Array.isArray(cart.items)) {
        ok = cart.items.some(
          (it) =>
            Array.isArray(it.discounts) &&
            it.discounts.some((d) => (d.title || '').toLowerCase() === lc)
        );
      }

      if (!ok) {
        if (msg) {
          msg.textContent = 'Ce code promo est invalide ou non applicable.';
          msg.classList.add('is-error');
        }
        return;
      }

      await this.reloadSection();
      this.initAll();
      this.open();
    } catch (err) {
      console.error(err);
      if (msg) {
        msg.textContent = 'Erreur réseau, veuillez réessayer.';
        msg.classList.add('is-error');
      }
    }
  }

  async removeDiscount() {
    const msg = this.querySelector('[data-discount-message]');
    try {
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ discount: '' }),
      });
      if (msg) {
        msg.textContent = '';
        msg.classList.remove('is-error', 'is-success');
      }
      await this.reloadSection();
      this.initAll();
      this.open();
    } catch (err) {
      console.error(err);
      if (msg) {
        msg.textContent = 'Impossible de retirer ce code.';
        msg.classList.add('is-error');
      }
    }
  }

  onQtyClick(btn) {
    btn.preventDefault?.();
    const wrap = btn.closest('[data-qty-wrap]');
    if (!wrap) return;

    const key = wrap.dataset.key;
    const cur = Number(wrap.dataset.qty || 1);
    const action = btn.dataset.action;
    const next = Math.max(0, action === 'inc' ? cur + 1 : cur - 1);

    this.updateLineItem(key, next);
  }

  async updateLineItem(key, quantity) {
    try {
      const r = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: key, quantity }),
      });
      if (!r.ok) return;
      document.dispatchEvent(new CustomEvent('cart:updated'));
    } catch (err) {
      console.error('SideCart updateLineItem error', err);
    }
  }

  async initDeliveryBlocks() {
    if (this._deliveryTimer) clearInterval(this._deliveryTimer);

    const blocks = this.querySelectorAll('[data-delivery]');
    if (!blocks.length) return;

    // Fetch delivery dates from API per product (deduplicated)
    const apiCache = {};
    const isLocal = ['127.0.0.1', 'localhost'].includes(window.location.hostname);
    for (const block of blocks) {
      const productId = block.dataset.productId;
      const apiEndpoint = isLocal ? block.dataset.apiEndpointLocal : block.dataset.apiEndpoint;
      if (!productId || !apiEndpoint || apiCache[productId]) continue;
      try {
        const res = await fetch(`${apiEndpoint}/api/delivery/estimate/${productId}`);
        if (res.ok) {
          const data = await res.json();
          if (data.success && (data.delivery_date_long || data.delivery_date)) {
            if (!data.delivery_date_long && data.delivery_date) {
              const d = new Date(data.delivery_date);
              data.delivery_date_long = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
            }
            if (!data.delivery_date_late_long && data.delivery_date_late) {
              const d = new Date(data.delivery_date_late);
              data.delivery_date_late_long = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
            }
            apiCache[productId] = data;
          }
        }
      } catch { /* fallback */ }
    }

    const tick = () => {
      const now = new Date();

      blocks.forEach((block) => {
        const apiData = apiCache[block.dataset.productId] || null;

        // Countdown
        const cutoffDeadline = apiData?.cutoff_deadline ? new Date(apiData.cutoff_deadline) : null;
        let effectiveDeadline;
        if (cutoffDeadline && cutoffDeadline > now) {
          effectiveDeadline = cutoffDeadline;
        } else {
          const cutoffHour = apiData?.cutoff_hour || Number(block.dataset.cutoffHour || 14);
          const deadlineToday = new Date(now);
          deadlineToday.setHours(cutoffHour, 0, 0, 0);
          effectiveDeadline = now < deadlineToday
            ? deadlineToday
            : new Date(deadlineToday.getTime() + 86400000);
        }

        const diffMs = Math.max(0, effectiveDeadline - now);
        const totalMin = Math.floor(diffMs / 60000);
        const days = Math.floor(totalMin / 1440);
        const hours = Math.floor((totalMin % 1440) / 60);
        const mins = totalMin % 60;
        const cd = block.querySelector('[data-countdown]');
        if (cd) {
          let text = 'dans les ';
          if (days > 0) text += `${days}j et ${hours}h`;
          else if (hours > 0) text += `${hours}h${String(mins).padStart(2, '0')}`;
          else text += `${mins}min`;
          cd.textContent = text;
        }

        // Delivery dates
        const dateEl = block.querySelector('[data-delivery-date]');
        const lateEl = block.querySelector('[data-delivery-date-late]');

        if (apiData) {
          if (dateEl) dateEl.textContent = apiData.delivery_date_long;
          if (lateEl && apiData.delivery_date_late_long) lateEl.textContent = apiData.delivery_date_late_long;
        } else {
          // Local fallback
          const cutoffHour = Number(block.dataset.cutoffHour || 14);
          const deadlineToday = new Date(now);
          deadlineToday.setHours(cutoffHour, 0, 0, 0);
          const beforeCutoff = now < deadlineToday;
          const productionDaysJson = block.dataset.atelierProductionDays;

          let deliveryDate, deliveryLateDate;
          if (productionDaysJson) {
            try {
              const atelierDelay = Number(block.dataset.atelierDeliveryDelay || 3);
              const vacStart = block.dataset.atelierVacationStart || null;
              const vacEnd = block.dataset.atelierVacationEnd || null;
              const result = this.calculateAtelierDelivery(now, productionDaysJson, atelierDelay, cutoffHour, vacStart, vacEnd);
              deliveryDate = result.deliveryDate;
            } catch {
              const delayDays = Number(block.dataset.delayDays || 5);
              const base = beforeCutoff ? now : new Date(now.getTime() + 86400000);
              deliveryDate = this.addBusinessDays(base, delayDays);
            }
          } else {
            const delayDays = Number(block.dataset.delayDays || 3);
            const base = beforeCutoff ? now : new Date(now.getTime() + 86400000);
            deliveryDate = this.addBusinessDays(base, delayDays);
          }
          const delayLateDays = Number(block.dataset.delayDaysLate || 15);
          deliveryLateDate = this.addBusinessDays(new Date(effectiveDeadline.getTime() + 60000), delayLateDays);

          if (dateEl) dateEl.textContent = this.formatDeliveryDate(deliveryDate);
          if (lateEl) lateEl.textContent = this.formatDeliveryDate(deliveryLateDate);
        }
      });
    };

    tick();
    this._deliveryTimer = setInterval(tick, 60000);
  }

  initGuaranteeSlider() {
    if (this._guaranteeTimer) clearInterval(this._guaranteeTimer);

    const wrap = this.querySelector('[data-sc-guarantee]');
    if (!wrap) return;

    const textEl = wrap.querySelector('.sc-guarantee__text');
    const iconEl = wrap.querySelector('.sc-guarantee__icon');
    const dataScript = wrap.querySelector('.sc-guarantee__data');
    const pagination = wrap.querySelector('[data-sc-guarantee-pagination]');
    if (!textEl || !dataScript || !pagination) return;

    let items;
    try {
      items = JSON.parse(dataScript.textContent || '[]');
    } catch {
      return;
    }
    items = (items || []).map((x) => (typeof x === 'string' ? { text: x, icon: '' } : x));
    if (!items.length) return;

    const delay = parseInt(wrap.dataset.autoplayDelay, 10) || 3000;
    wrap.style.setProperty('--sc-guarantee-delay', `${delay}ms`);

    pagination.innerHTML = items
      .map(
        (_, i) => `
          <div class="sc-guarantee__pagination-item ${i === 0 ? 'sc-guarantee__pagination-item--active' : ''}" data-index="${i}">
            <div class="sc-guarantee__pagination-bar">
              <div class="sc-guarantee__pagination-fill ${i === 0 ? 'animating' : ''}"></div>
            </div>
            <div class="sc-guarantee__pagination-dot"></div>
          </div>`
      )
      .join('');

    this._g = {
      wrap,
      items,
      textEl,
      iconEl,
      dots: wrap.querySelectorAll('.sc-guarantee__pagination-item'),
      i: 0,
      delay,
    };

    this.showGuarantee(0);
    this._guaranteeTimer = setInterval(() => this.showGuarantee((this._g.i + 1) % items.length), delay);
  }

  onGuaranteeClick(el) {
    const i = Number(el.dataset.index || 0);
    if (!this._g) return;
    this.showGuarantee(i % this._g.items.length);
    clearInterval(this._guaranteeTimer);
    this._guaranteeTimer = setInterval(
      () => this.showGuarantee((this._g.i + 1) % this._g.items.length),
      this._g.delay
    );
  }

  showGuarantee(i) {
    if (!this._g) return;
    const { items, textEl, iconEl, dots } = this._g;
    const it = items[i];
    if (!it) return;

    this._g.i = i;
    textEl.textContent = it.text || '';

    if (iconEl) {
      if (it.icon) {
        iconEl.src = it.icon;
        iconEl.style.display = '';
      } else {
        iconEl.style.display = 'none';
      }
    }

    dots.forEach((p, idx) => {
      const active = idx === i;
      p.classList.toggle('sc-guarantee__pagination-item--active', active);
      const fill = p.querySelector('.sc-guarantee__pagination-fill');
      if (!fill) return;
      fill.classList.remove('animating');
      if (active) {
        void fill.offsetWidth;
        fill.classList.add('animating');
      }
    });
  }

  async reloadSection() {
    const host = this.closest('[data-section-id]');
    const sectionId = host?.dataset.sectionId;
    if (!sectionId) return;

    const url = new URL(window.location.href);
    url.searchParams.set('sections', sectionId);

    const r = await fetch(url.toString(), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store',
    });
    if (!r.ok) return;

    const json = await r.json();
    const html = json[sectionId];
    if (!html) return;

    const tmp = document.createElement('div');
    tmp.innerHTML = html;

    const swap = (sel, how = 'inner') => {
      const n = tmp.querySelector(sel);
      const c = this.querySelector(sel);
      if (!n || !c) return;
      if (how === 'inner') c.innerHTML = n.innerHTML;
      else c.replaceWith(n);
    };

    swap('.side-cart__shipping-cart-information .side-cart__freeshipping');
    swap('#SideCartContent');
    swap('#SideCartFooter');
    const newTitle = tmp.querySelector('.title-sidecart');
    const curTitle = this.querySelector('.title-sidecart');
    if (newTitle && curTitle) curTitle.innerHTML = newTitle.innerHTML;

    const newBadge = tmp.querySelector('.header__cart-badge');
    if (newBadge) {
      document.querySelectorAll('.header__cart-badge').forEach((b) => (b.textContent = newBadge.textContent));
    }

    this.refreshRefs();
    this.rebindCloseButtons();
  }

  addBusinessDays(startDate, days) {
    const d = new Date(startDate);
    d.setHours(12, 0, 0, 0);
    let added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      const day = d.getDay();
      if (day !== 0 && day !== 6) added++;
    }
    if (d.getDay() === 6) d.setDate(d.getDate() + 2);
    if (d.getDay() === 0) d.setDate(d.getDate() + 1);
    return d;
  }

  formatDeliveryDate(date) {
    const weekday = date.toLocaleDateString('fr-FR', { weekday: 'long' });
    const day = String(date.getDate()).padStart(2, '0');
    const month = date.toLocaleDateString('fr-FR', { month: 'long' });
    return `Le ${weekday.charAt(0).toUpperCase() + weekday.slice(1)} ${day} ${month}`;
  }

  findNextProductionDay(fromDate, jsProductionDays) {
    const d = new Date(fromDate);
    for (let i = 0; i < 14; i++) {
      d.setDate(d.getDate() + 1);
      if (jsProductionDays.includes(d.getDay())) return new Date(d);
    }
    return null;
  }

  calculateAtelierDelivery(now, productionDaysJson, deliveryDelayDays, cutoffHour, vacationStart, vacationEnd) {
    let isoDays = JSON.parse(productionDaysJson);
    // Handle double-encoded JSON strings from metaobjects
    if (typeof isoDays === 'string') isoDays = JSON.parse(isoDays);
    const jsProductionDays = isoDays.map(d => d % 7);

    const deadlineToday = new Date(now);
    deadlineToday.setHours(cutoffHour, 0, 0, 0);

    const todayJs = now.getDay();
    const isTodayProduction = jsProductionDays.includes(todayJs);
    const beforeCutoff = now < deadlineToday;

    let shipDate;
    if (isTodayProduction && beforeCutoff) {
      shipDate = new Date(now);
      shipDate.setHours(12, 0, 0, 0);
    } else {
      shipDate = this.findNextProductionDay(now, jsProductionDays);
    }

    if (!shipDate) return { deliveryDate: this.addBusinessDays(now, deliveryDelayDays), shipDate: now };

    if (vacationStart && vacationEnd) {
      const vStart = new Date(vacationStart + 'T00:00:00');
      const vEnd = new Date(vacationEnd + 'T23:59:59');
      if (shipDate >= vStart && shipDate <= vEnd) {
        shipDate = this.findNextProductionDay(vEnd, jsProductionDays);
        if (!shipDate) return { deliveryDate: this.addBusinessDays(now, deliveryDelayDays), shipDate: now };
      }
    }

    const deliveryDate = this.addBusinessDays(shipDate, deliveryDelayDays);
    return { deliveryDate, shipDate };
  }

  open() {
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.body.classList.add('no-scroll');

    this.closePreviewPopup()
  }

  close() {
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('no-scroll');

    this.closePreviewPopup()
  }

  isOpen() {
    return this.classList.contains('is-open');
  }
}

if (!customElements.get('side-cart')) customElements.define('side-cart', SideCart);
